//dependencies
import React, { useEffect } from "react";
import { To, useNavigate, useParams } from "react-router-dom";

// components
import Button from "../../../components/Button";
import ContentModal from "../../../components/Modal/Templates/ContentModal";
import Spacer from "../../../components/Spacer";
import ComponentLoader from "../../../components/Spinner/ComponentLoader";
import Typography from "../../../components/Text";
import ActivityStreamPanel from "./components/ActivityStreamPanel";
import GoalProgressDateModal from "./components/ActivityStreamPanel/components/GoalProgress/components/GoalProgressDateModal";
import Checklist from "./components/Checklist";
import CompleteGoalModal from "./components/Modals/CompleteGoalModal";
import ShareGoalModal from "./components/Modals/ShareGoalModal";
import UpdateProgressModal from "./components/Modals/UpdateProgressModal";
import SingleGoalHeader from "./components/SingleGoalHeader";
import SingleGoalInfo from "./components/SingleGoalInfo";

import ErrorPage from "../../../pages/Error";

import {
	useCompanySettings,
	useDeleteGoal,
	useGoalDetails,
	useGoalHistory,
	useMutateGoalDetails,
	useRbacObjectPermissions,
} from "../../../api/hooks";
import useSavedPageState from "../../../hooks/usePageSavedPageState";
//hooks
import usePageWidthBreakpoint from "../../../hooks/usePageWidthBreakpoint";
import useSearchQueryParams from "../../../hooks/useSearchQueryParams";

//templates
import BasePageTemplate from "../../../pageTemplates/BasePageTemplate";
//css
import css from "./SingleGoal.module.scss";
//config
import Config from "./config";

const SingleGoal = () => {
	const navigate = useNavigate();
	const { isDesktop, isTablet, isMobile } = usePageWidthBreakpoint();
	const { goal_id: goalId } = useParams<{ goal_id: string }>();

	const {
		queryParams: { modal },
		handleQueryChange,
	} = useSearchQueryParams({
		schema: Config.queryParametersSchema,
	});

	const {
		pageState: { isActivityStreamClosed },
		updatePageState,
		isSavedPageStateLoading,
	} = useSavedPageState({
		pageName: "goal_progress",
		schema: Config.savedQueryParametersSchema,
	});

	const { isLoading: isCompanySettingsLoading } = useCompanySettings();

	const {
		data,
		isLoading: isInitialGoalLoading,
		refetch: refetchGoal,
		isError,
		error,
	} = useGoalDetails({ goalID: goalId || "", disableDefaultErrorHandling: true });

	const { isLoading: isInitialRbacLoading } = useRbacObjectPermissions("goal", goalId || "");
	const { isLoading: isGoalProgressHistoryInitialLoading, refetch: refetchProgress } = useGoalHistory({
		goalID: goalId || "",
		type: "progress",
		sortDirection: "desc",
		sortName: "as_of",
		disableDefaultErrorHandling: true,
	});

	const { isLoading: isGoalHistoryInitialLoading, refetch: refetchActivity } = useGoalHistory({
		goalID: goalId || "",
		sortDirection: "desc",
		sortName: "_id",
		disableDefaultErrorHandling: true,
	});

	const { mutate, isPending } = useMutateGoalDetails(goalId || "", {
		onSuccess: () => {
			handleQueryChange({ modal: undefined });
			refetchGoal();
			refetchProgress();
			refetchActivity();
		},
	});

	const { mutate: deleteGoal, isPending: isDeleting } = useDeleteGoal({
		onSuccess: () => {
			// we need to determine history stack length, if length is 1, we need to navigate to the dashboard page, else go back
			const historylength = window?.history?.length;
			// -1 is a valid "to" value, but the TS compiler doesn't allow it
			const navigateTo: To = historylength > 1 ? (-1 as To) : "/";

			navigate(navigateTo);
		},
	});

	useEffect(() => {
		if (!isActivityStreamClosed) {
			if (isMobile || isTablet) {
				updatePageState({ isActivityStreamClosed: true }, { save: false });
			}
		}
	}, [isDesktop, isTablet]);

	const closeModal = () => handleQueryChange({ modal: undefined, editHistoryEntryId: undefined });
	const goal = data?.goal;

	if (isError) {
		return <ErrorPage code={error?.response?.status || 500} />;
	}

	if (
		!goal?._id ||
		isInitialGoalLoading ||
		isInitialRbacLoading ||
		isGoalProgressHistoryInitialLoading ||
		isGoalHistoryInitialLoading ||
		isCompanySettingsLoading ||
		isSavedPageStateLoading
	) {
		return <ComponentLoader />;
	}

	return (
		<BasePageTemplate title={goal?.name || "Performyard Goal"}>
			<div className={css.singleGoalContainer}>
				<div className={css.mainContentContainer}>
					<Spacer height={2} />
					<SingleGoalHeader />
					<Spacer height={2} />
					<SingleGoalInfo />
					<Spacer height={2} />
					<Checklist />
					{isTablet || isMobile ? (
						<Button
							className={css.activityButton}
							onClick={() => {
								updatePageState({ isActivityStreamClosed: undefined }, { save: false });
							}}
						>
							Show Activity
						</Button>
					) : null}
				</div>
				<div className={css.slideoutContainer}>
					<ActivityStreamPanel />
				</div>
			</div>
			<UpdateProgressModal />
			<CompleteGoalModal />

			<ContentModal
				isOpen={modal === "archive"}
				handleClose={closeModal}
				title={
					<div className={css.modalContent}>
						<Typography size="sub-heading" weight="semi-bold">
							Archive Goal?
						</Typography>
						<Typography color="neutral-4" size="paragraph-2" weight="regular">
							{goal?.name}
						</Typography>
					</div>
				}
				buttons={[
					<Button key="cancel-archive-goal" testid="cancel-archive-goal" onClick={closeModal}>
						Cancel
					</Button>,
					<Button
						key="archive-goal"
						testid="archive-goal"
						color="green"
						isRequesting={isPending}
						onClick={() => {
							mutate([{ field: "status", value: "archived" }]);
						}}
					>
						Yes, Archive Goal
					</Button>,
				]}
			>
				<div className={css.modalContent}>
					<Typography className={css.modalContent} size="paragraph-1" weight="semi-bold">
						Are you sure you want to archive this goal? Archiving a goal will remove any alignment to this
						goal.
					</Typography>
				</div>
			</ContentModal>

			<ContentModal
				isOpen={modal === "delete"}
				handleClose={closeModal}
				title={
					<div className={css.modalContent}>
						<Typography size="sub-heading" weight="semi-bold">
							Delete Goal?
						</Typography>
						<Typography color="neutral-4" size="paragraph-2" weight="regular">
							{goal?.name}
						</Typography>
					</div>
				}
				buttons={[
					<Button key="cancel-delete-goal" testid="cancel-delete-goal" onClick={closeModal}>
						Cancel
					</Button>,
					<Button
						key="delete-goal"
						testid="delete-goal"
						color="green"
						isRequesting={isDeleting}
						onClick={() => {
							deleteGoal(goalId || "");
						}}
					>
						Yes, Delete Goal
					</Button>,
				]}
			>
				<Typography className={css.modalContent} size="paragraph-1" weight="semi-bold">
					Are you sure you want to delete this goal?
				</Typography>
			</ContentModal>
			<ShareGoalModal isOpen={modal === "share"} handleClose={closeModal} />
			<GoalProgressDateModal isOpen={modal === "edit-as-of-date"} handleClose={closeModal} />
		</BasePageTemplate>
	);
};

export default SingleGoal;
